import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import CallToAction from '../../components/common/CallToAction';
import Page from '../../components/Page';
import { FontAwesomeIcon } from '../../../node_modules/@fortawesome/react-fontawesome';
import { getBlogWithFallback } from '../../actions/blog/index';
import _ from 'lodash';
import moment from 'moment';
import line_logo from '../../assets/img/beautrium/social_logo/line_logo.png';
import fb_logo from '../../assets/img/beautrium/social_logo/fb_logo.png';
import twitter_logo from '../../assets/img/beautrium/social_logo/twitter_logo.png';
import ShareSideBar from '../../components/common/ShareSideBar';
import { PUBLIC_URL } from '../../config';
import { blogShare } from '../../actions/blog/index';
import ProductCard from '../../components/product/ProductCard';
import ImageGallery from 'react-image-gallery';
import RelatedProductList from '../../views/blog/blog-render/RelatedProductList';
import "react-image-gallery/styles/scss/image-gallery.scss";

// Frontload data fetching
const frontLoad = async props => {
    const blog_slug = props.match.params.id;
    return await props.getBlogWithFallback(blog_slug);
};

// Loading skeleton styles
const loadingStyles = {
    shimmer: {
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        WebkitAnimation: 'shimmer 1.5s infinite', // Add vendor prefix
    },
    skeletonText: {
        height: '1rem',
        borderRadius: '4px',
        margin: '0.5rem 0',
    },
    skeletonTitle: {
        height: '2.5rem',
        width: '80%',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    skeletonSubtitle: {
        height: '1.5rem',
        width: '60%',
        borderRadius: '4px',
        marginBottom: '2rem',
    },
    skeletonImage: {
        width: '100%',
        height: '400px',
        borderRadius: '8px',
        marginBottom: '2rem',
    },
    skeletonParagraph: {
        height: '1rem',
        borderRadius: '4px',
        marginBottom: '0.75rem',
    }
};

// Add shimmer animation styles as a string that can be used in both SSR and client
const shimmerKeyframes = `
    @-webkit-keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`;

// Add shimmer styles to the component
const shimmerStyles = `${shimmerKeyframes}`;

class BlogContent extends PureComponent {

    constructor(props) {
        super(props);
        this.fbShare = this.fbShare.bind(this);
        this.lineShare = this.lineShare.bind(this);
        this.twitterShare = this.twitterShare.bind(this);
        this.handleShare = this.handleShare.bind(this);
        this.renderPhotoByLayout = this.renderPhotoByLayout.bind(this);
        this.renderContentByType = this.renderContentByType.bind(this);
        this.renderBlogContent = this.renderBlogContent.bind(this);
        this.renderContentLegacy = this.renderContentLegacy.bind(this);
        this.renderContent = this.renderContent.bind(this);
    }

    // ===== Helper methods for clean render and minor perf gains =====
    getTitle(blog) {
        return blog.title_th || blog.blog_title || blog.title || '';
    }

    getCoverImage(blog) {
        return blog.cover_image?.url || blog.cover_image || '';
    }

    getAuthorName(blog, isStrapi) {
        if (isStrapi) {
            return (blog.author && (blog.author.penname || blog.author.name)) || '';
        }
        if (![555, 4].includes(blog.user_id) && blog.first_name && blog.last_name) {
            return `${blog.first_name} ${blog.last_name}`;
        }
        return '';
    }

    getTextContent(blog, isStrapi) {
        if (!blog) return '';
        if (!isStrapi) {
            if (!blog.content || typeof blog.content !== 'string') return '';
            const begin = blog.content.indexOf('<p>');
            if (begin === -1) return '';
            const start = begin + 3;
            const tail = blog.content.substr(start);
            const end = tail.indexOf('<');
            return end !== -1 ? tail.substr(0, end) : tail.substr(0, 200);
        }
        let textContent = blog.description || '';
        if (!textContent && blog.content && Array.isArray(blog.content)) {
            const textItem = blog.content.find(item => item.__component === 'blog.text');
            if (textItem && typeof textItem.text === 'string') {
                textContent = textItem.text.replace(/<[^>]*>/g, '').substring(0, 200);
            }
        }
        return textContent;
    }

    buildBlogJsonLd(blog, isStrapi, title, coverImage, description, pageUrl) {
        const published = blog.published_at || blog.created_at || blog.created_date;
        const modified = blog.updated_at || published;
        const authorName = this.getAuthorName(blog, isStrapi);

        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
            headline: title,
            description,
            datePublished: published ? moment(published).toISOString() : undefined,
            dateModified: modified ? moment(modified).toISOString() : undefined,
            publisher: { "@type": "Organization", name: "BEAUTRIUM" },
        };
        if (coverImage) jsonLd.image = [coverImage];
        if (authorName) jsonLd.author = { "@type": "Person", name: authorName };
        Object.keys(jsonLd).forEach((k) => { if (jsonLd[k] === undefined) delete jsonLd[k]; });
        return jsonLd;
    }

    renderBlogBody(blog) {
        return blog.isStrapi ? this.renderContent() : this.renderContentLegacy();
    }

    componentDidMount() {
        // Only add shimmer styles in browser environment
        if (typeof window !== 'undefined') {
            const styleTag = document.createElement('style');
            styleTag.innerHTML = shimmerStyles;
            document.head.appendChild(styleTag);
        }
    }

    componentDidUpdate(prevProps) {
        //After user click share
        if (prevProps.share_result === null && this.props.share_result === true) {
            const blogKey = this.props.content.isStrapi ? this.props.content.slug : this.props.location.blog_key;
            this.props.getBlogWithFallback(blogKey);
        }
    }

    renderTag(tags) {
        if (!tags || tags.length === 0) return null;

        return _.map(tags, (tag, index) => {
            const tagName = tag.name || tag.tag_name;
            if (index + 1 === tags.length) {
                return <span key={index}> {tagName} </span>
            } else {
                return <span key={index}> {tagName}, </span>
            }
        })
    }

    renderRelateProduct(products) {
        if (!products || !products.data || products.data.length === 0) return null;

        return _.map(products.data, (product, index) => {
            return (
                <div key={index} className="col-6 col-md-3 col-xl-2">
                    <ProductCard item={product} />
                </div>
            )
        })
    }

    fbShare(url) {
        if (typeof window === 'undefined') return;
        /* eslint-disable */
        if (typeof FB !== 'undefined') {
            FB.ui({
                method: 'share',
                href: url,
            }, function (response) { });
        }
        /* eslint-enable */
        this.handleShare('fb_share', this.props.content.fb_share)
    }

    lineShare(url) {
        if (typeof window === 'undefined') return;
        let temp = `https://social-plugins.line.me/lineit/share?url=${url}`
        window.open(temp, null, "width=503,height=510,left=50%,top=50%,resizable=yes,scrollbars=no,chrome=yes,centerscreen=yes")
        this.handleShare('line_share', this.props.content.line_share)
    }

    twitterShare(url, title) {
        if (typeof window === 'undefined') return;
        let temp = `https://twitter.com/intent/tweet?text=${title + " " + url}&amp;hashtags=BEAUTRIUM`
        window.open(temp, null, "width=503,height=510,left=50%,top=50%,resizable=yes,scrollbars=no,chrome=yes,centerscreen=yes")
        this.handleShare('twitter_share', this.props.content.twitter_share)
    }

    handleShare(platform, share) {
        const blogKey = this.props.content.isStrapi ? this.props.content.slug : this.props.location.blog_key;
        this.props.blogShare(blogKey, platform, share, this.props.content.total_share)
    }

    renderPhotoByLayout(content) {
        const blog = this.props.content;
        switch (content.layout) {
            case "STANDARD":
                return (
                    <div style={{ width: "100%" }}>
                        <img style={{ margin: "auto", display: "block", maxWidth: "100%" }} alt={blog.title_th || blog.blog_title} src={content.content[0].imgUrl} />
                    </div>
                )
            case "BEFORE_AFTER":
                return (
                    <div style={{ display: "flex" }}>
                        <div className="content-image-holder" style={{ width: "49.5%", marginRight: "1%" }}>
                            <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[0].imgUrl} />
                        </div>
                        <div className="content-image-holder" style={{ width: "49.5%" }}>
                            <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[1].imgUrl} />
                        </div>
                    </div>
                )
            case "BIG_TOP_TWO_BOT":
                return (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ paddingTop: "41.67%", position: "relative", marginBottom: "1%" }} >
                            <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[0].imgUrl} />
                        </div>
                        <div style={{ display: "flex" }}>
                            <div style={{ paddingTop: "41.67%", width: "49.5%", position: "relative", marginRight: "1%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[1].imgUrl} />
                            </div>
                            <div style={{ paddingTop: "41.67%", width: "49.5%", position: "relative" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[2].imgUrl} />
                            </div>
                        </div>
                    </div>
                )
            case "BIG_LEFT_TWO_RIGHT":
                return (
                    <div style={{ display: "flex" }}>
                        <div style={{ width: "49.5%", height: "0", paddingTop: "83.334%", position: "relative", marginRight: "1%" }}>
                            <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[0].imgUrl} />
                        </div>
                        <div style={{ width: "49.5%" }}>
                            <div style={{ width: "100%", paddingTop: "83.334%", position: "relative", marginBottom: "1.55%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[1].imgUrl} />
                            </div>
                            <div style={{ width: "100%", paddingTop: "83.334%", position: "relative" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[2].imgUrl} />
                            </div>
                        </div>
                    </div>
                )
            case "FOUR_GRID":
                return (
                    <div style={{ display: "flex", flexWrap: "wrap" }} >
                        <div style={{ width: "49.5%", marginBottom: "1%", marginRight: "1%" }}>
                            <div style={{ width: "100%", position: "relative", paddingTop: "83.334%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[0].imgUrl} />
                            </div>
                        </div>
                        <div style={{ width: "49.5%", marginBottom: "1%" }}>
                            <div style={{ width: "100%", position: "relative", paddingTop: "83.334%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[1].imgUrl} />
                            </div>
                        </div>
                        <div style={{ width: "49.5%", marginRight: "1%" }}>
                            <div style={{ width: "100%", position: "relative", paddingTop: "83.334%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[2].imgUrl} />
                            </div>
                        </div>
                        <div style={{ width: "49.5%" }}>
                            <div style={{ width: "100%", position: "relative", paddingTop: "83.334%" }}>
                                <img className="content-image" alt={blog.title_th || blog.blog_title} src={content.content[3].imgUrl} />
                            </div>
                        </div>
                    </div>
                )
            case "GALLERY":
                let gallery_content = _.map(content.content, (content) => {
                    return { original: content.imgUrl, thumbnail: content.imgUrl }
                })
                return (
                    <ImageGallery items={gallery_content} showPlayButton={false} showFullscreenButton={false} />
                )
            default:
                return null
        }
    }

    renderContentByType(content, index, adKey) {
        switch (content.type) {
            case "TEXT":
                return (
                    <div className="content-holder">
                        <div className="content"
                            dangerouslySetInnerHTML={{ __html: content.html }}
                        >
                        </div>
                    </div>
                )
            case "PHOTO":
                return (
                    <div className="content-holder">
                        <div className="content">
                            {this.renderPhotoByLayout(content)}
                        </div>
                    </div>
                )
            case "VIDEO":
                return (
                    <div className="content-holder">
                        <div className="content">
                            <div className="col-md-12">
                                <div className="blog-video-holder" style={{ position: "relative", paddingBottom: "56.25%", height: "0" }}>
                                    <iframe title="video" src={content.content} style={{ position: "absolute", top: "0", left: "0", width: "100%", height: "100%" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case "RELATED_PRODUCT":
                return (
                    <div className="content-holder">
                        <div className="content">
                            <div className="d-flex nowrap-scroll products-scroll">
                                <RelatedProductList matVarId={content.content} />
                            </div>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    renderBlogContent(arr_content) {
        let adIndex = 0;
        return (
            _.map(arr_content, (content, index) => {
                return (
                    <React.Fragment key={`blog_${index}`}>
                        <div className="content-block mb-2">
                            {this.renderContentByType(content, index, adIndex - 1)}
                        </div>
                    </React.Fragment>
                )
            })
        )
    }

    // New render method for Strapi API content
    renderContent() {
        const blog = this.props.content;
        if (!blog) return null;

        const pathname = this.props.location.pathname;
        let shareURL = PUBLIC_URL + pathname;
        let encodeShareURL = encodeURIComponent(shareURL);
        const date = moment(blog.published_at || blog.created_at).format("LLLL");
        const title = blog.title_th || blog.title;
        const coverImage = blog.cover_image?.url || blog.cover_image;
        const author = blog.author?.penname || '';

        return (
            <div id="homepage" className="container blog-content" >
                <div className="row">
                    <div onClick={this.props.history.length > 2 ? this.props.history.goBack : () => this.props.history.push(`/blogs`)} style={{ cursor: "pointer", marginLeft: "15px" }}>
                        <FontAwesomeIcon icon={["fal", "chevron-left"]} />&nbsp;Back
                    </div>
                </div>
                <div className="row content pt-2 mb-3">
                    <div className="col-12 mb-2">
                        <h1>{title}</h1>
                        <h4 className="color-light-gray">{author ? `${author} | ` : ""}{date}</h4>
                    </div>
                    <div className="col-md-3 offset-md-1 d-md-none mb-2">
                        <h4 className="color-light-gray" style={{ textAlign: "left", display: "inline-block" }}><FontAwesomeIcon icon={["fas", "share-alt"]} /> {blog.total_share || 0} |
                        </h4>
                        <div style={{ display: "inline-block", marginLeft: "5px" }}>
                            <div style={{ display: "flex" }}>
                                <div className="social-logo-holder mobile">
                                    <img alt="facebook" src={fb_logo} style={{ width: "100%" }} onClick={(e) => { e.preventDefault(); this.fbShare(shareURL) }}></img>
                                </div>
                                <div className="social-logo-holder mobile">
                                    <img alt="twitter" src={twitter_logo} style={{ width: "100%" }} onClick={(e) => { e.preventDefault(); this.twitterShare(encodeShareURL, title) }}></img>
                                </div>
                                <div className="social-logo-holder mobile" onClick={(e) => { e.preventDefault(); this.lineShare(shareURL) }}>
                                    <img alt="line" src={line_logo} style={{ width: "100%" }}></img>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-5 blog-content-block">
                        <img alt={title} className="blog-cover mb-md-4 mb-2" src={coverImage} />
                        <div className="contents">
                            {this.renderStrapiContent(blog.content)}
                        </div>
                    </div>
                    <div className="blog-footer">
                        {blog.tags && blog.tags.length > 0 && <div className="col-12 mt-3 pb-3 border-bottom-1 border-lightgray">
                            <span className="bold color-black">Tags: </span>{this.renderTag(blog.tags)}
                        </div>}
                        {blog.relate_products && blog.relate_products.data && blog.relate_products.data.length > 0 && <div className="col-12 pb-3 pt-5">
                            <span className="bold color-black">Relate product: </span>
                            <div className="row pt-2 row nowrap-scroll products-scroll">
                                {this.renderRelateProduct(blog.relate_products)}
                            </div>
                        </div>}
                    </div>
                </div>

            </div>
        )
    }

    // Render Strapi API content
    renderStrapiContent(content) {
        if (!content || !Array.isArray(content)) return null;

        return content.map((item, index) => {
            switch (item.__component) {
                case 'blog.text':
                    return (
                        <div key={`strapi_text_${index}`} className="content-holder">
                            <div className="content" dangerouslySetInnerHTML={{ __html: item.text }} />
                        </div>
                    );
                case 'blog.image-1':
                    return (
                        <div key={`strapi_image_${index}`} className="content-holder">
                            <div className="content mb-3">
                                <img
                                    src={item.image?.url || item.image?.formats?.large?.url || item.image?.formats?.medium?.url}
                                    alt={item.image?.alternativeText || ''}
                                    style={{ maxWidth: "100%", height: "auto" }}
                                />
                            </div>
                        </div>
                    );
                case 'blog.relate-items':
                    return (
                        <div key={`strapi_products_${index}`} className="content-holder">
                            <div className="content">
                                <div className="d-flex nowrap-scroll products-scroll">
                                    <RelatedProductList
                                        itemIds={item.products?.map(p => p.item_id).filter(id => id)}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                case 'blog.divider':
                    return (
                        <div key={`strapi_divider_${index}`} className="content-holder">
                            <hr style={{ borderColor: item.color || '#ddd' }} />
                        </div>
                    );
                default:
                    return null;
            }
        });
    }

    // Legacy render method (renamed from original renderContent)
    renderContentLegacy() {
        const blog = this.props.content;
        if (!blog) return null;

        const pathname = this.props.location.pathname;
        let shareURL = PUBLIC_URL + pathname;
        let encodeShareURL = encodeURIComponent(shareURL);
        const date = moment(blog.created_date).format("LLLL");
        let legacy = blog.render === "legacy" ? true : false;

        return (
            <div id="homepage" className="container blog-content" >
                <div className="row">
                    <div onClick={this.props.history.length > 2 ? this.props.history.goBack : () => this.props.history.push(`/blogs`)} style={{ cursor: "pointer", marginLeft: "15px" }}>
                        <FontAwesomeIcon icon={["fal", "chevron-left"]} />&nbsp;Back
                    </div>
                </div>
                <div className="row content pt-2 mb-3">
                    <div className="col-12 mb-2">
                        <h1>{blog.blog_title}</h1>
                        <h4 className="color-light-gray">{![555, 4].includes(blog.user_id) ? blog.first_name + " " + blog.last_name + " | " : ""}{date}</h4>
                    </div>
                    <div className="col-md-3 offset-md-1 d-md-none mb-2">
                        <h4 className="color-light-gray" style={{ textAlign: "left", display: "inline-block" }}><FontAwesomeIcon icon={["fas", "share-alt"]} /> {blog.total_share} |
                        </h4>
                        <div style={{ display: "inline-block", marginLeft: "5px" }}>
                            <div style={{ display: "flex" }}>
                                <div className="social-logo-holder mobile">
                                    <img alt="facebook" src={fb_logo} style={{ width: "100%" }} onClick={(e) => { e.preventDefault(); this.fbShare(shareURL) }}></img>
                                </div>
                                <div className="social-logo-holder mobile">
                                    <img alt="twitter" src={twitter_logo} style={{ width: "100%" }} onClick={(e) => { e.preventDefault(); this.twitterShare(encodeShareURL, blog.blog_title) }}></img>
                                </div>
                                <div className="social-logo-holder mobile" onClick={(e) => { e.preventDefault(); this.lineShare(shareURL) }}>
                                    <img alt="line" src={line_logo} style={{ width: "100%" }}></img>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-5 blog-content-block">
                        <img alt={blog.blog_title} className="blog-cover mb-md-4 mb-2" src={blog.cover_image} />
                        {legacy ? (
                            <div dangerouslySetInnerHTML={{ __html: blog.content }}></div>
                        )
                            : (<div className="contents">
                                {this.renderBlogContent(blog.content)}
                            </div>
                            )}
                    </div>
                    <div className="blog-footer">
                        {blog.tags && blog.tags.length > 0 && <div className="col-12 mt-3 pb-3 border-bottom-1 border-lightgray">
                            <span className="bold color-black">Tags: </span>{this.renderTag(blog.tags)}
                        </div>}
                        {blog.relate_products && blog.relate_products.data && blog.relate_products.data.length > 0 && <div className="col-12 pb-3 pt-5">
                            <span className="bold color-black">Relate product: </span>
                            <div className="row pt-2 row nowrap-scroll products-scroll">
                                {this.renderRelateProduct(blog.relate_products)}
                            </div>
                        </div>}
                    </div>
                </div>
                <ShareSideBar blogTitle={blog.blog_title} fbShare={blog.fb_share} lineShare={blog.line_share} twitterShare={blog.twitter_share} totalShare={blog.total_share} blogUrl={this.props.location.blog_key} />
            </div>
        )
    }

    renderLoadingSkeleton() {
        return (
            <div id="homepage" className="container blog-content">
                <div className="row">
                    <div style={{ cursor: "pointer", marginLeft: "15px", ...loadingStyles.shimmer, width: '80px', height: '24px', borderRadius: '4px' }}>
                    </div>
                </div>
                <div className="row content pt-2 mb-3">
                    <div className="col-12 mb-2">
                        <div style={{ ...loadingStyles.shimmer, ...loadingStyles.skeletonTitle }}></div>
                        <div style={{ ...loadingStyles.shimmer, ...loadingStyles.skeletonSubtitle }}></div>
                    </div>
                    <div className="col-md-3 offset-md-1 d-md-none mb-2">
                        <div style={{ ...loadingStyles.shimmer, width: '200px', height: '24px', borderRadius: '4px' }}></div>
                    </div>
                    <div className="mb-5 blog-content-block" style={{ width: '100%' }}>
                        <div style={{ ...loadingStyles.shimmer, ...loadingStyles.skeletonImage }}></div>
                        <div className="contents">
                            {/* Multiple paragraph skeletons */}
                            {[1, 2, 3, 4].map((_, index) => (
                                <div key={index} style={{ ...loadingStyles.shimmer, ...loadingStyles.skeletonParagraph, width: index % 2 ? '85%' : '100%' }}></div>
                            ))}
                            {/* Image placeholder */}
                            <div style={{ ...loadingStyles.shimmer, height: '250px', borderRadius: '8px', margin: '2rem 0' }}></div>
                            {/* More paragraph skeletons */}
                            {[1, 2].map((_, index) => (
                                <div key={index + 4} style={{ ...loadingStyles.shimmer, ...loadingStyles.skeletonParagraph, width: index % 2 ? '90%' : '95%' }}></div>
                            ))}
                        </div>
                    </div>
                    <div className="blog-footer">
                        <div className="col-12 mt-3 pb-3 border-bottom-1 border-lightgray">
                            <div style={{ ...loadingStyles.shimmer, width: '60%', height: '24px', borderRadius: '4px' }}></div>
                        </div>
                        <div className="col-12 pb-3 pt-5">
                            <div style={{ ...loadingStyles.shimmer, width: '200px', height: '24px', borderRadius: '4px', marginBottom: '1rem' }}></div>
                            <div className="row pt-2">
                                {[1, 2, 3, 4].map((_, index) => (
                                    <div key={index} className="col-6 col-md-3 col-xl-2">
                                        <div style={{ ...loadingStyles.shimmer, height: '200px', borderRadius: '8px', marginBottom: '1rem' }}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const blog = this.props.content;
        const isLoading = this.props.isLoading;

        if (isLoading) {
            return (
                <Page id="blog" title="Loading...">
                    {this.renderLoadingSkeleton()}
                </Page>
            );
        }

        if (!blog) {
            return (
                <Page id="blog" title="Blog Not Found">
                    <div className="container">
                        <div className="text-center py-5">
                            <h1>Blog Not Found</h1>
                            <p>The blog you're looking for doesn't exist.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => this.props.history.push('/blogs')}
                            >
                                Back to Blogs
                            </button>
                        </div>
                    </div>
                </Page>
            );
        }

        const isStrapi = blog.isStrapi;
        const title = this.getTitle(blog);
        const coverImage = this.getCoverImage(blog);
        const textContent = this.getTextContent(blog, isStrapi);
        const pageUrl = PUBLIC_URL + this.props.location.pathname;
        const blogJsonLd = this.buildBlogJsonLd(blog, isStrapi, title, coverImage, textContent, pageUrl);

        return (
            <Page id="blog" title={title} image={coverImage} description={textContent} jsonLd={blogJsonLd}>
                {this.renderBlogBody(blog)}
                <div className="container-fluid px-0" style={{ margin: "30px 0" }}>
                    <CallToAction />
                </div>
            </Page>
        );
    }
}

function mapStateToProps(state) {
    return {
        content: state.blog.content,
        share_result: state.blog.shareBlogResult,
        isLoading: state.blog.isLoading,
    }
}

export default connect(mapStateToProps, { getBlogWithFallback, blogShare })(
    frontloadConnect(frontLoad, {
        noServerRender: false,
        onMount: true,
        onUpdate: false
    })(BlogContent)
);

